import { apiGetCustomers } from "@lib/api/customer";
import { apiGetBillingWalletAddress } from "@lib/api/wallet";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { walletAddressTypeImage } from "@lib/helpers/ColorHelper";
import { UseMutationProps } from "@lib/types/Api";
import { BillingHistory } from "@lib/types/Billing";
import { Customer } from "@lib/types/Customer";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Image, Input, Modal, Select, Space } from "antd";
import { useCallback, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import useIntl from "react-intl/lib/src/components/useIntl";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props<T> = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  mutationQuery: UseMutationProps<Partial<T>>;
  addMultipleQuery: UseMutationProps<Partial<T>[]>;
  customer?: Customer;
};

export default function BillingPaymentRequestModal<T extends BillingHistory>({
  openModal,
  toggleModal,
  mutationQuery,
  addMultipleQuery,
  customer,
}: Props<T>) {
  const { messages } = useIntl();
  const [form] = Form.useForm();
  const customerId = customer?.id || Form.useWatch("customerId", form);

  const { mutate, isPending } = mutationQuery;

  const initialValues = {
    customerId: customer?.id,
  };

  const handleResetForm = useCallback(() => {
    form?.resetFields();
  }, []);

  const handleClose = useCallback(() => {
    toggleModal(false);
    handleResetForm();
  }, [toggleModal, form]);

  useEffect(() => {
    if (openModal) {
      handleResetForm();
    }
  }, [openModal]);

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: openModal,
  });

  const { data: walletAddress, isLoading: walletAddressIsLoading } = useQuery({
    queryFn: () =>
      apiGetBillingWalletAddress({
        limit: 100,
        filter: [`customerId||$eq||${customerId}`],
      }),
    queryKey: ["wallet-address-dropdown", customerId],
    enabled: openModal && !!customerId,
  });

  return (
    <Modal
      title={
        <FormattedMessage
          id={"common.createTitle"}
          values={{
            title: messages["sidebar.blockchainPaymentRequest"] as string,
          }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending || addMultipleQuery.isPending}
    >
      <Form<T>
        {...layout}
        form={form}
        name={"sidebar.blockchainPaymentRequest-modal"}
        onFinish={(values) => {
          if (values?.walletAddressId === "all") {
            const items = walletAddress?.data?.data?.map((address) => ({
              customerId: customer?.id || values?.customerId,
              walletAddressId: address.id,
            })) as Partial<T>[];
            addMultipleQuery.mutate(items);
          } else {
            mutate({
              ...values,
              customerId: customer?.id || values?.customerId,
            });
          }
        }}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={initialValues}
      >
        <Form.Item
          name="customerId"
          label={messages["common.customer"] as string}
          rules={[{ required: true }]}
        >
          {customer ? (
            <>
              <Input type="hidden" />
              <CustomerName customer={customer} />
            </>
          ) : (
            <Select
              showSearch
              loading={customersIsLoading}
              onChange={() => {
                form.setFieldValue("walletAddressId", "all");
              }}
              optionFilterProp="text"
              options={customers?.data?.data?.map((customer) => ({
                label: <CustomerName customer={customer} />,
                text: customer.name,
                value: customer.id,
              }))}
            />
          )}
        </Form.Item>

        <Form.Item
          name="walletAddressId"
          label={messages["common.walletAddress"] as string}
          rules={[{ required: true }]}
        >
          <Select
            showSearch
            loading={walletAddressIsLoading}
            optionFilterProp="text"
            options={[
              {
                label: messages["common.all"] as string,
                text: messages["common.all"] as string,
                value: "all",
              },

              ...(walletAddress?.data?.data?.map((item) => ({
                label: (
                  <>
                    <Image
                      src={walletAddressTypeImage(item?.network)}
                      style={{ width: "15px", height: "15px" }}
                    />{" "}
                    {item?.address}
                  </>
                ),
                text: item?.address,
                value: item?.id,
              })) || []),
            ]}
          />
        </Form.Item>

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            <Button
              htmlType="button"
              onClick={handleClose}
              disabled={isPending || addMultipleQuery.isPending}
            >
              {messages["common.reset"] as string}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isPending || addMultipleQuery.isPending}
            >
              {messages["common.create"] as string}
            </Button>
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
